<template>
  <div>
    <StandardPageLayout
      heading="Contact"
      heading-color="text-los-blue-darkest"
      layout-width="lg"
      img-wide
      img="/deco/doorkijkwinkel.webp"
      img-alt="Een doorkijk van de winkel."
      img-border-color="bg-los-purple-base"
      pattern="lijnsteek"
      :breadcrumbs-links="[{ title: 'Contact', slug: '/contact' }]"
    >
      <template #introduction>
        <div class="flex flex-col gap-y-8 justify-between h-full">
          <div class="grid grid-cols-[auto_1fr] gap-y-2 gap-x-4 items-center">
            <div class="w-6 flex justify-center">
              <FontAwesome
                icon="clock"
                class="text-lg text-los-purple-darkest"
              />
            </div>
            <h4
              class="font-platform text-lg sm:text-xl text-los-purple-darkest"
            >
              Onze openingstijden
            </h4>
            <div
              class="leading-relaxed col-start-2 text-sm min-[400px]:text-base min-[400px]:leading-loose"
            >
              <div>Maandag: Gesloten</div>
              <div>
                Dinsdag t/m vrijdag:
                <span class="inline-block">9:00 – 18:00 uur</span>
              </div>
              <div>Zaterdag: 9:00 – 16:00 uur</div>
              <div>Zondag: Gesloten</div>
            </div>
          </div>

          <div class="grid grid-cols-[auto_1fr] gap-y-2 gap-x-4 items-center">
            <div class="w-6 flex justify-center">
              <FontAwesome
                icon="house"
                class="text-lg text-los-purple-darkest"
              />
            </div>
            <h4
              class="font-platform text-lg sm:text-xl text-los-purple-darkest"
            >
              Adres
            </h4>
            <div
              class="leading-relaxed col-start-2 text-sm min-[400px]:text-base min-[400px]:leading-loose"
            >
              <a
                class="hover:underline"
                href="https://maps.app.goo.gl/XQEHdpQjWkuosmjz8"
                target="_blank"
              >
                <div>
                  <span> Langeweg 4-B, 2371 EG<br />Roelofarendsveen </span>
                </div>
              </a>
            </div>
          </div>

          <div class="grid grid-cols-[auto_1fr] gap-y-2 gap-x-4 items-center">
            <div class="w-6 flex justify-center">
              <FontAwesome
                icon="phone"
                class="text-lg text-los-purple-darkest"
              />
            </div>
            <h4
              class="font-platform text-lg sm:text-xl text-los-purple-darkest"
            >
              Neem contact met ons op
            </h4>
            <div
              class="leading-relaxed col-start-2 text-sm min-[400px]:text-base min-[400px]:leading-loose"
            >
              <div>
                <a href="tel:(071)3031232" class="hover:underline"
                  >Telefonisch: 071 303 12 32</a
                >
              </div>
              <div>
                <a
                  href="https://wa.me/31713031232"
                  target="_blank"
                  class="hover:underline"
                  >Whatsapp: 071 303 12 32</a
                >
              </div>
              <div>
                <a href="mailto:info@losnaaimachines.nl" class="hover:underline"
                  >E-mail: info@losnaaimachines.nl</a
                >
              </div>
            </div>
          </div>
        </div>
      </template>
    </StandardPageLayout>

    <div class="w-full py-basic relative mt-4">
      <div class="w-full mx-auto max-w-screen-lg px-4 sm:px-8 md:px-10">
        <h2 class="heading-basic text-los-purple-darkest mb-5 font-semibold">
          Speciale openingstijden
        </h2>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8 lg:gap-10"
        >
          <div v-for="day in holidays">
            <div class="font-semibold mb-1 text-los-purple-darkest">
              • {{ day.name }}
            </div>
            <div class="text-sm font-medium capitalize">
              {{ formatHolidayDay(day.date) }}:&nbsp;
              <span v-if="day.closed">Gesloten</span>
              <span v-else
                >{{ formatTime(day.time_open) }} -
                {{ formatTime(day.time_close) }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="w-full py-basic bg-los-purple-lightest relative">
      <div class="w-full mx-auto max-w-screen-lg px-4 sm:px-8 md:px-10">
        <h2 class="heading-basic mb-5 text-los-pink-darkest font-semibold">
          Leuk als je langskomt!
        </h2>
        <p class="text-basic max-w-3xl mb-8">
          Los Naaimachines is centraal gelegen in Roelofarendsveen en is goed
          bereikbaar met zowel de auto als met het ov. De winkel is slechts 3
          minuten rijden vanaf de A4. Ook is er een bushalte vlakbij met een
          directe verbinding vanuit Leiden en Schiphol.
        </p>
        <button @click="showRouteModal = true" class="btn-pink-outline">
          Bekijk onze bereikbaarheid
          <FontAwesome icon="chevron-right" class="text-sm" />
        </button>
      </div>
    </div>

    <div class="w-full px-basic my-basic">
      <div class="w-full mx-auto max-w-screen-xl">
        <div class="mx-auto max-w-screen-md">
          <h2 class="heading-basic mb-5 text-los-blue-darkest font-semibold">
            Vragen, opmerkingen of fanmail?
          </h2>

          <form
            @submit.prevent="onSubmitForm"
            @change="onChangeForm"
            class="w-full grid grid-cols-8 gap-5 border-2 border-dashed border-los-blue-light bg-los-blue-lightest py-8 px-4 sm:p-8 md:p-10 rounded-2xl shadow"
          >
            <h3 class="font-medium text-lg md:text-xl col-span-full mb-6">
              Stuur ons een mail en we nemen spoedig contact met je op. Je kan
              ons ook altijd bellen.
            </h3>
            <ClientOnly>
              <div class="col-span-full md:col-span-4">
                <label for="firstname" class="font-medium"
                  >Voornaam <span class="text-los-pink-dark">*</span></label
                >
                <input
                  v-model="form.firstname"
                  type="text"
                  id="firstname"
                  key="firstname"
                  name="firstname"
                  class="w-full px-3 py-2 mt-1 outline-none rounded-lg bg-white bg-white border-none"
                />
              </div>
              <div class="col-span-full md:col-span-4">
                <label for="lastname" class="font-medium"
                  >Achternaam <span class="text-los-pink-dark">*</span></label
                >
                <input
                  v-model="form.lastname"
                  type="text"
                  id="lastname"
                  key="lastname"
                  name="lastname"
                  class="w-full px-3 py-2 mt-1 outline-none rounded-lg bg-white bg-white border-none"
                />
              </div>
              <div class="col-span-full">
                <label for="email" class="font-medium"
                  >E-mailadres <span class="text-los-pink-dark">*</span></label
                >
                <input
                  v-model="form.email"
                  type="text"
                  key="email"
                  id="email"
                  name="email"
                  class="w-full px-3 py-2 mt-1 outline-none rounded-lg bg-white bg-white border-none"
                />
              </div>

              <div class="col-span-full">
                <label for="message" class="font-medium"
                  >Uw bericht <span class="text-los-pink-dark">*</span></label
                >
                <textarea
                  v-model="form.message"
                  type="text"
                  key="message"
                  id="message"
                  name="message"
                  rows="5"
                  class="w-full px-3 py-2 mt-1 outline-none rounded-lg bg-white bg-white border-none"
                ></textarea>
              </div>
            </ClientOnly>
            <div
              class="col-span-full flex items-center justify-end gap-x-6 mt-4 sm:mt-6"
            >
              <div class="text-los-pink-darkest">
                {{ validationErrorMessage }}
              </div>

              <button
                v-if="!finishedForm"
                type="submit"
                class="btn-blue"
                :class="{ disabled: loading }"
                :disabled="loading"
              >
                <span v-if="!loading">Verzenden</span>
                <span v-else>Bezig...</span>
              </button>

              <div v-else class="btn-blue-outline">
                <FontAwesome icon="check" class="text-sm mr-1" /> Ontvangen
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <ModalContainer v-if="showRouteModal" @close="showRouteModal = false">
        <h3 class="font-platform heading-basic text-los-purple-darkest">
          Bereikbaarheid
        </h3>
        <p class="text-basic">
          Je kan Los Naaimachines gemakkelijk bereiken met zowel de auto als het
          ov. De winkel kijkt uit op de watertoren van Roelofarendsveen, te
          herkennen aan de grote tulpen op de toren.
        </p>
        <p class="text-basic">
          <strong class="text-los-purple-darkest">Met de auto:</strong> Vanaf de
          A4 rijd je in 3 minuten vanaf de afrit 5 (Roelofarendsveen) naar Los
          Naaimachines. Je kan gratis parkeren op het parkeerterrein tegenover
          de winkel.<br />
          <strong class="text-los-purple-darkest">Met het ov:</strong> Neem bus
          365 (vanaf Leiden Centraal of Schiphol) of 56 (vanaf Leiden Centraal
          of Leimuiden) en stap uit bij bushalte Stationsstraat,
          Roelofarendsveen. De winkel is op 5 minuten loopafstand van de
          bushalte.
        </p>
      </ModalContainer>
    </Teleport>
  </div>
</template>

<script setup>
import dayjs from 'dayjs';

const { find, create } = useStrapi();

const toastsStore = useToastsStore();

const validationErrorMessage = ref('');
const loading = ref(false);
const finishedForm = ref(false);
const showRouteModal = ref(false);

const defaultFormValues = {
  firstname: null,
  lastname: null,
  email: null,
  message: null,
};

const form = reactive({ ...defaultFormValues });

const { data } = await useAsyncData('contact-page', () =>
  find('contact-page', {
    populate: ['seo', 'seo.metaImage'],
  })
);

usePageSeo(data.value.data?.seo ?? {});

const { data: holidaysData } = await useAsyncData('holidays', () =>
  find('holidays', {
    filters: {
      date: {
        $gte: dayjs().format(),
      },
    },
    sort: ['date:asc'],
  })
);

const holidays = computed(() => {
  return holidaysData.value.data;
});

function formatHolidayDay(date) {
  return dayjs(date).format('dddd D MMMM');
}

function formatTime(time) {
  return time.substring(0, time.length - 3);
}

function validateForm() {
  const foundFalsyField = Object.values(form).findIndex((v) => {
    return !v;
  });

  if (foundFalsyField !== -1) {
    validationErrorMessage.value = 'Alle velden moeten worden ingevuld.';
    return false;
  }

  return true;
}

function resetFormValues() {
  Object.assign(form, defaultFormValues);
}

function onChangeForm() {
  validationErrorMessage.value = '';
  finishedForm.value = false;
}

async function onSubmitForm() {
  if (!validateForm()) {
    return;
  }

  try {
    loading.value = true;

    await create('contact-forms', form);

    resetFormValues();
    toastsStore.setSuccessToast({
      title:
        'We hebben je bericht ontvangen en nemen spoedig contact met je op.',
    });
    finishedForm.value = true;
  } catch (err) {
    toastsStore.setFailedToast({
      title: 'Oeps! Er ging iets verkeerd.',
    });
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss" scoped></style>
